import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { shareReplay, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiResponse, Patient, Visit, FaceScanResponse,
  RegisterPatientRequest, CreateVisitRequest,
  AddBillRequest, AddDoctorNoteRequest,
  DepartmentCheckInRequest, Bill, DoctorNote, DepartmentCheckIn
} from '../models/patient.model';
import { AutoBillRequest, AutoBillResponse, InvoiceDto } from '../models/billing.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  // API base URL from environment configuration
  // Automatically switches between localhost (dev) and Azure (prod)
  private baseUrl = environment.apiUrl;

  // ── Cache for static/slow-changing data ───────────────────────────
  private cache = new Map<string, { data$: Observable<any>; timestamp: number }>();
  private defaultCacheTTL = 600000; // 10 minutes

  constructor(private http: HttpClient) {}

  /**
   * Returns a cached observable if available and within TTL, otherwise calls the factory.
   * Uses shareReplay(1) so multiple subscribers share the same HTTP request.
   */
  private getCached(key: string, factory: () => Observable<any>, ttlMs?: number): Observable<any> {
    const ttl = ttlMs ?? this.defaultCacheTTL;
    const existing = this.cache.get(key);
    if (existing && (Date.now() - existing.timestamp) < ttl) {
      return existing.data$;
    }
    const data$ = factory().pipe(shareReplay(1));
    this.cache.set(key, { data$, timestamp: Date.now() });
    return data$;
  }

  /** Clear a specific cache entry (call after mutations that invalidate it) */
  clearCache(key: string): void {
    this.cache.delete(key);
  }

  /** Clear all cached data (call on login/logout) */
  clearAllCache(): void {
    this.cache.clear();
  }

  // ═══════════════════════════════════════════
  // PATIENT APIs
  // ═══════════════════════════════════════════

  /**
   * Registers a new patient.
   * Called from the patient registration form.
   */
  registerPatient(request: RegisterPatientRequest): Observable<ApiResponse<Patient>> {
    return this.http.post<ApiResponse<Patient>>(
      `${this.baseUrl}/patients/register`, request
    );
  }

  /**
   * Scans a face photo and returns patient details.
   * This is the MAIN feature — called from face scanner screen.
   */
  scanFace(photoBase64: string): Observable<ApiResponse<FaceScanResponse>> {
    return this.http.post<ApiResponse<FaceScanResponse>>(
      `${this.baseUrl}/patients/face-scan`,
      { photoBase64 }
    );
  }

  /**
   * Gets a patient by their ID with full history.
   */
  getPatientById(id: number): Observable<ApiResponse<Patient>> {
    return this.http.get<ApiResponse<Patient>>(
      `${this.baseUrl}/patients/${id}`
    );
  }

  /** DPDP right-to-erasure — anonymize a patient's PII (admin only; records kept de-identified). */
  erasePatient(id: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/patients/${id}/erase`, {});
  }

  /**
   * Searches patients by name or phone number.
   * Fallback when face scan doesn't work.
   */
  searchPatients(term: string): Observable<ApiResponse<Patient[]>> {
    const params = new HttpParams().set('term', term);
    return this.http.get<ApiResponse<Patient[]>>(
      `${this.baseUrl}/patients/search`, { params }
    );
  }

  /**
   * Ranked "find existing patient" (PATIENT_SEARCH_SPEC Phase 1) — scores candidates by
   * phone/DOB/phonetic-name so staff REUSE the record instead of creating a duplicate.
   * Returns { topScore, band: 'HIGH'|'MEDIUM'|'LOW', candidates: [...] }.
   */
  findPatientCandidates(opts: { name?: string; dob?: string; phone?: string; gender?: string }): Observable<any> {
    // TC-5142 (2026-11-03): sent in the body, because a web address with a phone number in it ends up in logs.
    return this.http.post<any>(`${this.baseUrl}/patients/find`, {
      name: opts.name?.trim() || null, dob: opts.dob?.trim() || null, phone: opts.phone?.trim() || null, gender: opts.gender?.trim() || null,
    });
  }

  /**
   * Gets all patients with server-side search, filter, and pagination.
   */
  getAllPatients(page = 1, pageSize = 20, search?: string, gender?: string): Observable<any> {
    let params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    if (gender) {
      params = params.set('gender', gender);
    }
    return this.http.get<any>(`${this.baseUrl}/patients`, { params });
  }

  // ═══════════════════════════════════════════
  // VISIT APIs
  // ═══════════════════════════════════════════

  /**
   * Creates a new visit when patient arrives.
   */
  createVisit(request: CreateVisitRequest): Observable<ApiResponse<Visit>> {
    return this.http.post<ApiResponse<Visit>>(
      `${this.baseUrl}/visits`, request
    );
  }

  /**
   * Closes a visit when patient leaves.
   */
  checkOutVisit(visitId: number, force = false, reason?: string): Observable<ApiResponse<Visit>> {
    const params = force ? `?force=true&reason=${encodeURIComponent(reason || '')}` : '';
    return this.http.put<ApiResponse<Visit>>(
      `${this.baseUrl}/visits/${visitId}/checkout${params}`, {}
    );
  }

  /** GET /api/visits/{id}/checkout-readiness (legacy — string-based) */
  getCheckoutReadiness(visitId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/visits/${visitId}/checkout-readiness`);
  }

  /**
   * GET /api/patients/{id}/timeline — full chronological journey across every visit
   * (registration, vitals, notes, tests, payments, refunds, dispenses, admissions,
   * discharges, security events). Used by the Timeline tab in Patient History.
   */
  getPatientTimeline(patientId: number, opts?: {
    visitId?: number;
    from?: string;
    to?: string;
    limit?: number;
  }): Observable<any> {
    const params: string[] = [];
    if (opts?.visitId) params.push(`visitId=${opts.visitId}`);
    if (opts?.from) params.push(`from=${encodeURIComponent(opts.from)}`);
    if (opts?.to) params.push(`to=${encodeURIComponent(opts.to)}`);
    if (opts?.limit) params.push(`limit=${opts.limit}`);
    const q = params.length ? '?' + params.join('&') : '';
    return this.http.get<any>(`${this.baseUrl}/patients/${patientId}/timeline${q}`);
  }

  /**
   * GET /api/visits/{id}/checkout/validate — structured pre-flight validation.
   * Returns: { canCheckout, needsForce, blockingReasons[], warnings[],
   *           pendingFinancials, pendingDepartments[], rolesAllowedToOverride[] }
   * MUST be called before clicking Final Checkout. The frontend renders blockers
   * as an itemized modal — never as a generic "checkout failed" toast.
   */
  validateCheckout(visitId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/visits/${visitId}/checkout/validate`);
  }

  /**
   * Gets full visit details.
   */
  getVisitById(visitId: number): Observable<ApiResponse<Visit>> {
    return this.http.get<ApiResponse<Visit>>(
      `${this.baseUrl}/visits/${visitId}`
    );
  }

  /**
   * Gets paginated visit list for a patient (lightweight projection).
   * ✅ PERF: Returns VisitListItem[] (no nested bills/notes/tests)
   */
  getPatientVisits(patientId: number, page: number = 1, pageSize: number = 20): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/visits/patient/${patientId}?page=${page}&pageSize=${pageSize}`
    );
  }

  /**
   * Records patient entering a department.
   */
  departmentCheckIn(request: DepartmentCheckInRequest): Observable<ApiResponse<DepartmentCheckIn>> {
    return this.http.post<ApiResponse<DepartmentCheckIn>>(
      `${this.baseUrl}/visits/department-checkin`, request
    );
  }

  /**
   * Adds a bill item to a visit.
   */
  addBill(request: AddBillRequest): Observable<ApiResponse<Bill>> {
    return this.http.post<ApiResponse<Bill>>(
      `${this.baseUrl}/visits/bill`, request
    );
  }

  /**
   * Marks a bill as paid.
   */
  payBill(billId: number): Observable<ApiResponse<Bill>> {
    return this.http.put<ApiResponse<Bill>>(
      `${this.baseUrl}/visits/bill/${billId}/pay`, {}
    );
  }

  /**
   * Gets bill summary for a visit.
   */
  getBillSummary(visitId: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.baseUrl}/visits/${visitId}/bill-summary`
    );
  }

  /**
   * Adds doctor note to a visit.
   */
  addDoctorNote(request: AddDoctorNoteRequest): Observable<ApiResponse<DoctorNote>> {
    return this.http.post<ApiResponse<DoctorNote>>(
      `${this.baseUrl}/visits/doctor-note`, request
    );
  }

  getActiveVisits() {
    return this.http.get<any>(`${this.baseUrl}/visits/active`);
  }

  // getBillingSync() and getPaymentLog() — REMOVED (Session 2, May 2026).
  // Both methods only fed the Emergency Dashboard page (now deleted).
  // Backend endpoints `/api/visits/billing-sync` and
  // `/api/test-requests/payment-log` removed in tandem. No other UI page
  // referenced them (verified via grep before removal).

  /** GET /api/visits?assignedDoctorStaffId={id} — doctor's patient queue */
  getDoctorVisits(staffId: number) {
    return this.http.get<any>(`${this.baseUrl}/visits?assignedDoctorStaffId=${staffId}`);
  }

  /** GET /api/visits/{id} — full visit details with notes, tests, bills */
  getVisitDetails(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/visits/${visitId}`);
  }

  /** GET /api/visits/doctor/{staffId}/completed?range=today|yesterday|week|month|all */
  getDoctorCompleted(staffId: number, range: string = 'today') {
    return this.http.get<any>(`${this.baseUrl}/visits/doctor/${staffId}/completed?range=${range}`);
  }

  /** GET /api/auth/doctors — active doctors list for hospital (cached 10 min) */
  getDoctors() {
    return this.getCached('doctors', () =>
      this.http.get<any>(`${this.baseUrl}/auth/doctors`)
    );
  }

  /** POST /api/test-requests */
  createTestRequest(body: any) {
    return this.http.post<any>(`${this.baseUrl}/test-requests`, body);
  }

  /** POST /api/test-requests/batch — send multiple tests at once */
  createBatchTestRequest(body: any) {
    return this.http.post<any>(`${this.baseUrl}/test-requests/batch`, body);
  }

  /** PATCH /api/test-requests/{id}/complete */
  completeTestRequest(id: number, body: any) {
    return this.http.patch<any>(`${this.baseUrl}/test-requests/${id}/complete`, body);
  }

  /** POST /api/test-requests/{id}/report-to-follow — patient checks out now; result follows later */
  reportTestToFollow(id: number, body: { note?: string; expectedDate?: string; undo?: boolean } = {}) {
    return this.http.post<any>(`${this.baseUrl}/test-requests/${id}/report-to-follow`, body);
  }

  /** PATCH /api/test-requests/{id}/must-result-before-discharge — doctor safety flag */
  setMustResultBeforeDischarge(id: number, value: boolean) {
    return this.http.patch<any>(`${this.baseUrl}/test-requests/${id}/must-result-before-discharge`, { value });
  }

  /** GET /api/test-requests?department= */
  getTestRequests(department: string) {
    return this.http.get<any>(`${this.baseUrl}/test-requests?department=${department}`);
  }

  /** POST /api/doctor-notes */
  saveDoctorNote(body: any) {
    return this.http.post<any>(`${this.baseUrl}/doctor-notes`, body);
  }

  /** GET /api/visits/patient/{patientId} — paginated lightweight visit history
   *  ✅ PERF: Returns VisitListItem[] (no nested collections)
   */
  getPatientVisitHistory(patientId: number, page: number = 1, pageSize: number = 50) {
    return this.http.get<any>(`${this.baseUrl}/visits/patient/${patientId}?page=${page}&pageSize=${pageSize}`);
  }

  /** POST /api/visits/{id}/checkout — supports force + payment deferral */
  checkoutVisit(
    visitId: number,
    force = false,
    reason?: string,
    deferralType?: string,
    deferralNotes?: string
  ) {
    const params = force ? `?force=true&reason=${encodeURIComponent(reason || '')}` : '';
    const body: any = {};
    if (reason) body.reason = reason;
    if (deferralType) body.deferralType = deferralType;
    if (deferralNotes) body.deferralNotes = deferralNotes;
    return this.http.post<any>(`${this.baseUrl}/visits/${visitId}/checkout${params}`, body);
  }

  /** PATCH /api/visits/{id}/mark-status — Mark visit as NoShow or LAMA */
  markVisitStatus(visitId: number, status: string, reason?: string) {
    return this.http.patch<any>(`${this.baseUrl}/visits/${visitId}/mark-status`, { status, reason });
  }

  /** TC-5966: critical results this doctor has not acknowledged (survives a disconnect, unlike the live push). */
  getUnacknowledgedCritical() {
    return this.http.get<any>(`${this.baseUrl}/test-requests/critical-unacknowledged`);
  }

  /** TC-3079: every critical result in this hospital still waiting to be acknowledged (Lab dashboard). */
  getOpenCritical() {
    return this.http.get<any>(`${this.baseUrl}/test-requests/critical-open`);
  }

  /** PATCH /api/test-requests/{id}/acknowledge-critical — Doctor acknowledges critical lab result */
  acknowledgeCritical(testRequestId: number) {
    return this.http.patch<any>(`${this.baseUrl}/test-requests/${testRequestId}/acknowledge-critical`, {});
  }

  /** PUT /api/visits/{id}/doctor-status — Update doctor's "Seen" status (persists to DB) */
  updateDoctorStatus(visitId: number, doctorStatus: string) {
    return this.http.put<any>(`${this.baseUrl}/visits/${visitId}/doctor-status`, { doctorStatus });
  }

  // ═══════════════════════════════════════════
  // VITALS APIs (Nurse Dashboard)
  // ═══════════════════════════════════════════

  /** POST /api/vitals — nurse records vitals for a patient */
  recordVitals(body: any) {
    return this.http.post<any>(`${this.baseUrl}/vitals`, body);
  }

  /** PUT /api/vitals/{id} — nurse edits existing vitals (with audit trail) */
  updateVitals(vitalId: number, body: any) {
    return this.http.put<any>(`${this.baseUrl}/vitals/${vitalId}`, body);
  }

  /** GET /api/vitals/queue — all active patients (nurse dashboard queue) */
  getNurseQueue() {
    return this.http.get<any>(`${this.baseUrl}/vitals/queue`);
  }

  /** GET /api/vitals/visit/{visitId} — vitals history for a visit */
  getVitalsByVisit(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/vitals/visit/${visitId}`);
  }

  // ═══════════════════════════════════════════
  // MESSAGING APIs
  // ═══════════════════════════════════════════

  /** POST /api/messages — send a direct or dept message */
  sendMessage(body: any) {
    return this.http.post<any>(`${this.baseUrl}/messages`, body);
  }

  /** GET /api/messages/inbox — logged-in staff inbox */
  getInbox() {
    return this.http.get<any>(`${this.baseUrl}/messages/inbox`);
  }

  /** PATCH /api/messages/{id}/read */
  markMessageRead(id: number) {
    return this.http.patch<any>(`${this.baseUrl}/messages/${id}/read`, {});
  }

  /** GET /api/auth/staff — list of all staff for compose/direct-message recipient picker */
  getStaffList() {
    return this.http.get<any>(`${this.baseUrl}/auth/staff`);
  }

  /** GET /api/messages/thread/{staffId} — conversation thread with one staff member */
  getThread(staffId: number) {
    return this.http.get<any>(`${this.baseUrl}/messages/thread/${staffId}`);
  }


  // ═══════════════════════════════════════════
  // AMBULANCE APIs
  // ═══════════════════════════════════════════

  /** POST /api/ambulance/dispatch — admin dispatches a driver */
  dispatchAmbulance(body: any) {
    return this.http.post<any>(`${this.baseUrl}/ambulance/dispatch`, body);
  }

  /** PATCH /api/ambulance/{id}/status — driver updates their status */
  updateAmbulanceStatus(id: number, body: any) {
    return this.http.patch<any>(`${this.baseUrl}/ambulance/${id}/status`, body);
  }

  /** GET /api/ambulance/active — all active dispatches for hospital */
  getActiveDispatches() {
    return this.http.get<any>(`${this.baseUrl}/ambulance/active`);
  }

  /** GET /api/ambulance/my-dispatch — driver's current assignment */
  getMyDispatch() {
    return this.http.get<any>(`${this.baseUrl}/ambulance/my-dispatch`);
  }

  /** GET /api/ambulance/history — past 30 dispatches */
  getAmbulanceHistory() {
    return this.http.get<any>(`${this.baseUrl}/ambulance/history`);
  }

  // ═══════════════════════════════════════════
  // QUEUE APIs (Smart Queue / Wait Time)
  // ═══════════════════════════════════════════

  /** GET /api/queue/status — all active queues grouped by doctor with ETAs */
  getQueueStatus() {
    return this.http.get<any>(`${this.baseUrl}/queue/status`);
  }

  /** GET /api/queue/doctor/{staffId} — one doctor's queue with ETAs */
  getDoctorQueue(staffId: number) {
    return this.http.get<any>(`${this.baseUrl}/queue/doctor/${staffId}`);
  }

  /** GET /api/queue/public-link — hospital code for public queue URL */
  getQueuePublicLink() {
    return this.http.get<any>(`${this.baseUrl}/queue/public-link`);
  }

  /** QR image drawn by our own API (PNG data URL) — replaces the outside QR service (2026-09-28). */
  getQrDataUrl(text: string) {
    return this.http.get<any>(`${this.baseUrl}/qr`, { params: { text } });
  }

  /** PATCH /api/queue/{visitId}/start-seeing — doctor calls next patient */
  queueStartSeeing(visitId: number) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/start-seeing`, {});
  }

  /** PATCH /api/queue/{visitId}/send-to-lab — send patient to lab */
  queueSendToLab(visitId: number) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/send-to-lab`, {});
  }

  /** PATCH /api/queue/{visitId}/lab-returned — patient returned from lab (priority re-entry) */
  queueLabReturned(visitId: number) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/lab-returned`, {});
  }

  /** PATCH /api/queue/{visitId}/mark-as-seen — consultation complete, visit still active */
  queueMarkAsSeen(visitId: number) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/mark-as-seen`, {});
  }

  /** PATCH /api/queue/{visitId}/lab-no-show — patient did not return from lab */
  queueLabNoShow(visitId: number) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/lab-no-show`, {});
  }

  /** PATCH /api/queue/{visitId}/defer — patient defers to another day */
  queueDeferPatient(visitId: number, expectedDate?: string, reason?: string) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/defer`, { expectedDate, reason });
  }

  /** PATCH /api/queue/{visitId}/patient-arrived — deferred/no-show patient re-arrives */
  queuePatientArrived(visitId: number) {
    return this.http.patch<any>(`${this.baseUrl}/queue/${visitId}/patient-arrived`, {});
  }

  // ═══════════════════════════════════════════
  // AI DIAGNOSTIC ASSISTANT APIs
  // ═══════════════════════════════════════════

  /** POST /api/diagnostic-ai/suggest — get AI diagnostic suggestions */
  getDiagnosticSuggestions(visitId: number, chiefComplaints: string, symptoms: string, medicalHistory?: string) {
    return this.http.post<any>(`${this.baseUrl}/diagnostic-ai/suggest`, {
      visitId, chiefComplaints, symptoms, medicalHistory
    });
  }

  /** GET /api/diagnostic-ai/status — check if AI service is configured */
  getDiagnosticAIStatus() {
    return this.http.get<any>(`${this.baseUrl}/diagnostic-ai/status`);
  }

  // ═══════════════════════════════════════════
  // WHATSAPP APIs
  // ═══════════════════════════════════════════

  /** GET /api/whatsapp/status — check if WhatsApp Cloud API is configured */
  getWhatsAppStatus() {
    return this.http.get<any>(`${this.baseUrl}/whatsapp/status`);
  }

  /** POST /api/auth/reset-password (SuperAdmin only) */
  resetStaffPassword(email: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/auth/reset-password`, { email, newPassword });
  }

  /** POST /api/whatsapp/send-discharge/{visitId} — send discharge summary via WhatsApp API */
  sendWhatsAppDischarge(visitId: number) {
    return this.http.post<any>(`${this.baseUrl}/whatsapp/send-discharge/${visitId}`, {});
  }

  /** POST /api/whatsapp/send-message — send custom WhatsApp message */
  sendWhatsAppMessage(phone: string, message: string) {
    return this.http.post<any>(`${this.baseUrl}/whatsapp/send-message`, { phone, message });
  }

  // ═══════════════════════════════════════════
  // ANALYTICS APIs
  // ═══════════════════════════════════════════

  /** GET /api/analytics/overview — KPI cards */
  getAnalyticsOverview() {
    return this.http.get<any>(`${this.baseUrl}/analytics/overview`);
  }

  /** GET /api/analytics/daily?days=7 — day-by-day chart data */
  getAnalyticsDaily(days = 7) {
    return this.http.get<any>(`${this.baseUrl}/analytics/daily?days=${days}`);
  }

  /** GET /api/analytics/doctors — per-doctor stats */
  getAnalyticsDoctors() {
    return this.http.get<any>(`${this.baseUrl}/analytics/doctors`);
  }

  /** GET /api/analytics/peak-hours — busiest hours of day */
  getAnalyticsPeakHours() {
    return this.http.get<any>(`${this.baseUrl}/analytics/peak-hours`);
  }

  /** GET /api/analytics/departments — per-department visit counts */
  getAnalyticsDepartments() {
    return this.http.get<any>(`${this.baseUrl}/analytics/departments`);
  }

  // ── OPERATIONAL DASHBOARD (Session 4 PR2B, May 2026) ──────────────
  // 4 independent endpoints — fired in parallel via forkJoin on boot,
  // then refetched on their own cadences. Do NOT merge into a single
  // monolithic call — independent cacheability is the whole point.

  /** GET /api/dashboard/critical-alerts — top strip (no cache). */
  getDashboardCriticalAlerts() {
    return this.http.get<any>(`${this.baseUrl}/dashboard/critical-alerts`);
  }

  /** GET /api/dashboard/live-ops — paginated patient grid with dept filter + sort. */
  getDashboardLiveOps(
    dept: string = 'All',
    page: number = 1,
    pageSize: number = 50,
    sortKey?: string,
    sortDir?: string
  ) {
    const params: Record<string, string> = {
      dept, page: String(page), pageSize: String(pageSize)
    };
    if (sortKey) params['sortKey'] = sortKey;
    if (sortDir) params['sortDir'] = sortDir;
    return this.http.get<any>(`${this.baseUrl}/dashboard/live-ops`, { params });
  }

  /** GET /api/dashboard/finance-summary — Billed / Collected / Pending / Refunded. */
  getDashboardFinanceSummary() {
    return this.http.get<any>(`${this.baseUrl}/dashboard/finance-summary`);
  }

  /** GET /api/dashboard/action-center — severity-sorted action items. */
  getDashboardActionCenter() {
    return this.http.get<any>(`${this.baseUrl}/dashboard/action-center`);
  }

  /** GET /api/dashboard/department-load — per-dept load %, meta, tone. */
  getDashboardDepartmentLoad() {
    return this.http.get<any>(`${this.baseUrl}/dashboard/department-load`);
  }

  // ── BED MANAGEMENT ──────────────────────────────────────────────

  /** GET /api/beds — all beds grouped by ward */
  getBeds() {
    return this.http.get<any>(`${this.baseUrl}/beds`);
  }

  /** POST /api/beds — add a new bed */
  addBed(body: { bedNumber: string; ward: string; bedType: string }) {
    return this.http.post<any>(`${this.baseUrl}/beds`, body);
  }

  updateBed(id: number, body: { bedNumber: string; ward: string; bedType: string }) {
    return this.http.patch<any>(`${this.baseUrl}/beds/${id}`, body);
  }

  removeBed(id: number) {
    return this.http.delete<any>(`${this.baseUrl}/beds/${id}`);
  }

  /** PATCH /api/beds/{id}/assign — assign bed to patient */
  assignBed(id: number, body: { patientName: string; patientId?: number }) {
    return this.http.patch<any>(`${this.baseUrl}/beds/${id}/assign`, body);
  }

  /** PATCH /api/beds/{id}/release — free a bed */
  releaseBed(id: number) {
    return this.http.patch<any>(`${this.baseUrl}/beds/${id}/release`, {});
  }

  /** PATCH /api/beds/{id}/maintenance — mark bed for maintenance */
  setBedMaintenance(id: number, body: { notes?: string }) {
    return this.http.patch<any>(`${this.baseUrl}/beds/${id}/maintenance`, body);
  }

  // ── DRUG INTERACTION CHECK ───────────────────────────────────────

  /** POST /api/drug-interaction/check — check medications for allergy/interaction alerts */
  checkDrugInteractions(visitId: number, medications: string) {
    return this.http.post<any>(`${this.baseUrl}/drug-interaction/check`, { visitId, medications });
  }

  // ── MEDICINE AUTOCOMPLETE ──────────────────────────────────────

  /** GET /api/medicines/search?q=amo&department=fever — autocomplete drug names */
  searchMedicines(query: string, department?: string): Observable<any> {
    let url = `${this.baseUrl}/medicines/search?q=${encodeURIComponent(query)}`;
    if (department) url += `&department=${encodeURIComponent(department)}`;
    return this.http.get<any>(url);
  }

  // ──────────────────────────────────────────────────────────────────
  // HOSPITAL TESTS — Hospital Admin manages hospital's test catalog
  // ──────────────────────────────────────────────────────────────────

  /** GET /api/hospital-tests — List all active tests with optional filters (cached 10 min) */
  getHospitalTests(page = 1, pageSize = 50, department?: string): Observable<any> {
    const cacheKey = `hospital-tests-${page}-${pageSize}-${department || 'all'}`;
    return this.getCached(cacheKey, () => {
      let params = new HttpParams()
        .set('page', page)
        .set('pageSize', pageSize);
      if (department) {
        params = params.set('department', department);
      }
      return this.http.get<any>(`${this.baseUrl}/hospital-tests`, { params });
    });
  }

  /** POST /api/hospital-tests — Create a new hospital test */
  createHospitalTest(request: any): Observable<any> {
    this.clearCache('test-departments');
    // Clear all hospital-tests cache entries
    this.cache.forEach((_, key) => { if (key.startsWith('hospital-tests-')) this.cache.delete(key); });
    return this.http.post<any>(`${this.baseUrl}/hospital-tests`, request);
  }

  /** PATCH /api/hospital-tests/{id} — Update test details and price */
  updateHospitalTest(testId: number, request: any): Observable<any> {
    this.cache.forEach((_, key) => { if (key.startsWith('hospital-tests-')) this.cache.delete(key); });
    return this.http.patch<any>(`${this.baseUrl}/hospital-tests/${testId}`, request);
  }

  /** DELETE /api/hospital-tests/{id} — Deactivate test (soft delete) */
  deactivateHospitalTest(testId: number): Observable<any> {
    this.cache.forEach((_, key) => { if (key.startsWith('hospital-tests-')) this.cache.delete(key); });
    return this.http.delete<any>(`${this.baseUrl}/hospital-tests/${testId}`);
  }

  /** GET /api/hospital-tests/departments/list — Get unique departments for dropdown (cached 10 min) */
  getTestDepartments(): Observable<any> {
    return this.getCached('test-departments', () =>
      this.http.get<any>(`${this.baseUrl}/hospital-tests/departments/list`)
    );
  }

  // ── RECEPTIONIST DASHBOARD APIs ──────────────────────────────────────

  /** GET /api/receptionist/patient-status — Get all active patients with completion status */
  getPatientCompletionStatus() {
    return this.http.get<any>(`${this.baseUrl}/receptionist/patient-status`);
  }

  /** GET /api/receptionist/patient-status/{visitId} — Get specific patient completion status */
  getPatientCompletionStatusById(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/receptionist/patient-status/${visitId}`);
  }

  /** GET /api/receptionist/ready-for-checkout — Get patients ready for immediate checkout */
  getReadyForCheckoutPatients() {
    return this.http.get<any>(`${this.baseUrl}/receptionist/ready-for-checkout`);
  }

  /** POST /api/receptionist/checkout-patient/{visitId} — Checkout a patient */
  checkoutPatientRecept(visitId: number) {
    return this.http.post<any>(`${this.baseUrl}/receptionist/checkout-patient/${visitId}`, {});
  }

  /** GET /api/receptionist/dashboard-summary — Get dashboard summary statistics */
  getReceptionistDashboardSummary() {
    return this.http.get<any>(`${this.baseUrl}/receptionist/dashboard-summary`);
  }

  // ── FILE UPLOAD APIs ────────────────────────────────────────────────

  /** POST /api/files/upload — Upload file for a visit */
  uploadFile(visitId: number, department: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams()
      .set('visitId', visitId.toString())
      .set('department', department);
    // TC-6100: a large report (e.g. 20 MB) on a slow hospital link must not hit the 30s default
    // timeout and abort (a retry can then create a duplicate). Uploads get 5 minutes, as the
    // TimeoutInterceptor documents ("File uploads: X-Timeout: 300000").
    const headers = new HttpHeaders({ 'X-Timeout': '300000' });
    return this.http.post<any>(`${this.baseUrl}/files/upload`, formData, { params, headers });
  }

  /** GET /api/files/visit/{visitId} — List all files for a visit */
  getFilesForVisit(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/files/visit/${visitId}`);
  }

  /** DELETE /api/files/delete — Delete a file */
  deleteFile(fileUrl: string) {
    const params = new HttpParams().set('fileUrl', fileUrl);
    return this.http.delete<any>(`${this.baseUrl}/files/delete`, { params });
  }

  // ── HOSPITAL SETTINGS APIs ──────────────────────────────────────

  /** GET /api/hospital-settings — Get current hospital's settings (cached 10 min) */
  getHospitalSettings() {
    return this.getCached('hospital-settings', () =>
      this.http.get<any>(`${this.baseUrl}/hospital-settings`)
    );
  }

  /** PATCH /api/hospital-settings — Update hospital settings */
  updateHospitalSettings(settings: any) {
    this.clearCache('hospital-settings');
    return this.http.patch<any>(`${this.baseUrl}/hospital-settings`, settings);
  }

  /** POST /api/hospital-settings/reset-defaults — Reset to factory defaults */
  resetHospitalSettingsToDefaults() {
    return this.http.post<any>(`${this.baseUrl}/hospital-settings/reset-defaults`, {});
  }

  /** GET /api/hospital-settings/all — Get all hospitals' settings (SuperAdmin only) */
  getAllHospitalSettings() {
    return this.http.get<any>(`${this.baseUrl}/hospital-settings/all`);
  }

  /** GET /api/hospital-settings/{hospitalId} — Get specific hospital's settings (SuperAdmin only) */
  getHospitalSettingsById(hospitalId: number) {
    return this.http.get<any>(`${this.baseUrl}/hospital-settings/${hospitalId}`);
  }

  // ── OP VALIDITY CHECK ──────────────────────────────────────

  /** GET /api/visits/op-validity/{patientId} — Check if returning patient's OP is still valid */
  checkOPValidity(patientId: number) {
    return this.http.get<any>(`${this.baseUrl}/visits/op-validity/${patientId}`);
  }

  /** GET /api/visits/outstanding-bills/{patientId} — Unpaid bills from previous visits */
  getOutstandingBills(patientId: number) {
    return this.http.get<any>(`${this.baseUrl}/visits/outstanding-bills/${patientId}`);
  }

  // ── NOTIFICATIONS APIs ──────────────────────────────────────

  /** GET /api/notifications — Get all notifications for current staff */
  getNotifications() {
    return this.http.get<any>(`${this.baseUrl}/notifications`);
  }

  /** GET /api/notifications/unread-count — Get unread count for bell badge */
  getUnreadNotificationCount() {
    return this.http.get<any>(`${this.baseUrl}/notifications/unread-count`);
  }

  /** PATCH /api/notifications/{id}/read — Mark one notification as read */
  markNotificationRead(id: number) {
    return this.http.patch<any>(`${this.baseUrl}/notifications/${id}/read`, {});
  }

  /** PATCH /api/notifications/read-all — Mark all notifications as read */
  markAllNotificationsRead(keepCritical = false, ids?: number[]) {
    return this.http.patch<any>(`${this.baseUrl}/notifications/read-all${keepCritical ? '?keepCritical=true' : ''}`, ids ? { ids } : {});
  }

  // ── ADMISSIONS APIs (Inpatient management) ──────────────────────────────

  /** POST /api/admissions — Admit a patient from OPD to inpatient stay */
  admitPatient(request: any) {
    return this.http.post<any>(`${this.baseUrl}/admission`, request);
  }

  /** POST /api/admission/{id}/assign-bed — attach a bed to a ward-only admission (reception/nursing; per the Bed Assignment setting) */
  assignBedToAdmission(admissionId: number, bedId: number) {
    return this.http.post<any>(`${this.baseUrl}/admission/${admissionId}/assign-bed`, { bedId });
  }

  /** GET /api/admissions — Get all active admissions in the hospital */
  getAdmissions() {
    return this.http.get<any>(`${this.baseUrl}/admission`);
  }

  /** GET /api/admissions/doctor/{doctorId} — Get doctor's admitted patients */
  getDoctorAdmissions(doctorId: number) {
    return this.http.get<any>(`${this.baseUrl}/admission/doctor/${doctorId}`);
  }

  /** GET /api/admissions/visit/{visitId} — Check if visit has an admission */
  getAdmissionByVisit(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/admission/visit/${visitId}`);
  }

  /** GET /api/admissions/beds — Get available beds (optionally filtered by ward) */
  getAvailableBeds(ward?: string) {
    let params = new HttpParams();
    if (ward) {
      params = params.set('ward', ward);
    }
    return this.http.get<any>(`${this.baseUrl}/admission/beds`, { params });
  }

  /** PATCH /api/admissions/{id}/discharge — Discharge a patient */
  dischargeAdmission(id: number, request: any) {
    return this.http.patch<any>(`${this.baseUrl}/admission/${id}/discharge`, request);
  }

  /** POST /api/admission/{id}/withdraw-discharge-approval — the doctor takes back a discharge approval (TC-2488) */
  withdrawDischargeApproval(admissionId: number, reason: string) {
    return this.http.post<any>(`${this.baseUrl}/admission/${admissionId}/withdraw-discharge-approval`, { reason });
  }

  /** POST /api/refunds/over-collection/{visitId} — raise the refund for an out-patient who over-paid (TC-5862) */
  raiseOverCollectionRefund(visitId: number) {
    return this.http.post<any>(`${this.baseUrl}/refunds/over-collection/${visitId}`, {});
  }

  /** POST /api/admission/{id}/approve-discharge — Doctor approves discharge (two-step IP checkout) */
  approveDischarge(admissionId: number) {
    return this.http.post<any>(`${this.baseUrl}/admission/${admissionId}/approve-discharge`, {});
  }

  /** PATCH /api/admission/{id}/nursing-done — Nurse marks discharge prep done */
  markNursingDone(admissionId: number) {
    return this.http.patch<any>(`${this.baseUrl}/admission/${admissionId}/nursing-done`, {});
  }

  /** PATCH /api/admission/{id}/billing-cleared — Billing clears payment */
  markBillingCleared(admissionId: number) {
    return this.http.patch<any>(`${this.baseUrl}/admission/${admissionId}/billing-cleared`, {});
  }

  /** GET /api/admission/discharge-queue — All admissions in discharge pipeline grouped by stage */
  getDischargeQueue() {
    return this.http.get<any>(`${this.baseUrl}/admission/discharge-queue`);
  }

  /** PATCH /api/admission/{id}/payment-mode — change an admission's payer mid-stay (Self-Pay → Insurance etc.) */
  updateAdmissionPaymentMode(admissionId: number, payload: any) {
    return this.http.patch<any>(`${this.baseUrl}/admission/${admissionId}/payment-mode`, payload);
  }

  // ── ACCOUNTS RECEIVABLE (Deferred Bills) ───────────────────────

  /** GET /api/accounts-receivable — All deferred bills with aging & summary */
  getDeferredBills(status?: string) {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<any>(`${this.baseUrl}/accounts-receivable${params}`);
  }

  /** PATCH /api/accounts-receivable/{billId}/update — Update settlement status */
  updateSettlement(billId: number, data: any) {
    return this.http.patch<any>(`${this.baseUrl}/accounts-receivable/${billId}/update`, data);
  }

  /** GET /api/accounts-receivable/{billId}/events — settlement payment history (every settle action) */
  getSettlementEvents(billId: number) {
    return this.http.get<any>(`${this.baseUrl}/accounts-receivable/${billId}/events`);
  }

  /** GET /api/accounts-receivable/writeoffs/pending — write-offs above the limit waiting for a second person */
  getPendingWriteOffApprovals() {
    return this.http.get<any>(`${this.baseUrl}/accounts-receivable/writeoffs/pending`);
  }

  /** POST /api/accounts-receivable/writeoffs/{id}/approve — the second person books it */
  approveWriteOff(approvalId: number) {
    return this.http.post<any>(`${this.baseUrl}/accounts-receivable/writeoffs/${approvalId}/approve`, {});
  }

  /** POST /api/accounts-receivable/writeoffs/{id}/reject — nothing is written off */
  rejectWriteOff(approvalId: number, notes: string) {
    return this.http.post<any>(`${this.baseUrl}/accounts-receivable/writeoffs/${approvalId}/reject`, {}, { params: { notes } });
  }

  /** GET /api/accounts-receivable/write-offs — all bills written off as bad debt (review/reconciliation) */
  getWriteOffs() {
    return this.http.get<any>(`${this.baseUrl}/accounts-receivable/write-offs`);
  }

  // ── PATIENT JOURNEY TIMELINE ──────────────────────────────────

  /** GET /api/visits/{visitId}/timeline — Full patient journey timeline */
  getTimeline(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/visits/${visitId}/timeline`);
  }

  // ── NURSE OBSERVATIONS ────────────────────────────────────────

  /** POST /api/nurse/observations — Record daily observation */
  recordNurseObservation(request: any) {
    return this.http.post<any>(`${this.baseUrl}/nurse/observations`, request);
  }

  /** GET /api/nurse/observations/{admissionId} — Get observations for admission */
  getNurseObservations(admissionId: number) {
    return this.http.get<any>(`${this.baseUrl}/nurse/observations/${admissionId}`);
  }

  /** GET /api/nurse/observations/visit/{visitId} — Get observations by visit */
  getNurseObservationsByVisit(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/nurse/observations/visit/${visitId}`);
  }

  /** PUT /api/nurse/observations/{id} — Update observation */
  updateNurseObservation(id: number, request: any) {
    return this.http.put<any>(`${this.baseUrl}/nurse/observations/${id}`, request);
  }

  /** GET /api/visits/{visitId}/observations-timeline — Unified (nurse + doctor + vitals) role-tagged timeline */
  getObservationsTimeline(visitId: number) {
    return this.http.get<any>(`${this.baseUrl}/visits/${visitId}/observations-timeline`);
  }

  // ── OWNER DASHBOARD ────────────────────────────────────
getOwnerDashboardSnapshot(): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/snapshot`);
}

getTodayRevenue(): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/revenue/today`);
}

getYesterdayRevenue(): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/revenue/yesterday`);
}

getDoctorRevenue(top: number = 5, period: string = 'today'): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/doctors/revenue?top=${top}&period=${period}`);
}

getPatientCount(): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/patients/count`);
}

getPendingPayments(): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/payments/pending`);
}

getBedOccupancy(): Observable<any> {
  return this.http.get<any>(`${this.baseUrl}/owner-dashboard/beds/occupancy`);
}

// ── BILLING AUTOMATION endpoints ──────────────────────────────────────────

// Generate invoice for a visit: POST /api/billing-automation/auto-bill
autoBillOnCheckout(request: AutoBillRequest): Observable<AutoBillResponse> {
  return this.http.post<AutoBillResponse>(
    `${this.baseUrl}/billing-automation/auto-bill`,
    request  // request already has visitId inside it — no double spreading
  );
}

// Get full invoice details: GET /api/BillingAutomation/invoice/{billId}
// (2026-09-27: was `/billing-automation/…`, which 404s — BillingAutomationController is
//  [Route("api/[controller]")], so the segment is the controller name, not a kebab slug.)
getInvoiceDetails(billId: number): Observable<InvoiceDto> {
  return this.http.get<InvoiceDto>(
    `${this.baseUrl}/BillingAutomation/invoice/${billId}`
  );
}

// Send invoice via SMS/email: POST /api/BillingAutomation/invoice/{billId}/send
sendInvoice(billId: number, sms: boolean = false, email: boolean = true): Observable<any> {
  return this.http.post(
    `${this.baseUrl}/BillingAutomation/invoice/${billId}/send`,
    {},
    { params: { sms: sms.toString(), email: email.toString() } }
  );
}

  getAwaitingPaymentTests(): Observable<any> {
    return this.http.get(`${this.baseUrl}/test-requests/awaiting-payment`);
  }

  confirmLabPayment(testRequestId: number, body: {
    paymentMethod: string;
    paymentReference?: string;
    testPrice: number;
  }): Observable<any> {
    return this.http.patch(`${this.baseUrl}/test-requests/${testRequestId}/confirm-payment`, body);
  }

  confirmPharmacyPayment(testRequestId: number, body: {
    medicineCost: number;
    paymentMethod: string;
    paymentReference?: string;
    idempotencyKey?: string;
  }): Observable<any> {
    return this.http.patch(`${this.baseUrl}/test-requests/${testRequestId}/confirm-pharmacy-payment`, body);
  }

  getDispensedToday(): Observable<any> {
    return this.http.get(`${this.baseUrl}/test-requests/dispensed-today`);
  }

  // ── SCHEDULING — Future test orders ─────────────────────────
  /** GET /api/test-requests/upcoming?department=Lab&daysAhead=90 */
  getUpcomingTestRequests(department: string, daysAhead = 90): Observable<any> {
    return this.http.get(`${this.baseUrl}/test-requests/upcoming?department=${department}&daysAhead=${daysAhead}`);
  }

  /** GET /api/test-requests/overdue?department=Lab&daysBack=365 — past-due scheduled tests */
  getOverdueTestRequests(department: string, daysBack = 365): Observable<any> {
    return this.http.get(`${this.baseUrl}/test-requests/overdue?department=${department}&daysBack=${daysBack}`);
  }

  /** GET /api/test-requests/pending-followups/{patientId} */
  getPendingFollowUps(patientId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/test-requests/pending-followups/${patientId}`);
  }

  /** PATCH /api/test-requests/{id}/link-fulfillment */
  linkTestFulfillment(testRequestId: number, fulfillmentVisitId: number): Observable<any> {
    return this.http.patch(`${this.baseUrl}/test-requests/${testRequestId}/link-fulfillment`, { fulfillmentVisitId });
  }

  /** PATCH /api/test-requests/{id}/cancel-scheduled */
  cancelScheduledTest(testRequestId: number, reason: string): Observable<any> {
    return this.http.patch(`${this.baseUrl}/test-requests/${testRequestId}/cancel-scheduled`, { reason });
  }

  /** Cancel a test — routed through the refund workflow (Phase 1).
   *  Server creates a RefundTransaction if the test was paid; doctor only
   *  initiates — Lab confirms (or auto-confirms), Billing processes. */
  cancelTest(testRequestId: number, reason: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/refunds/test/${testRequestId}/cancel`, { reason });
  }

  // ── REFUND LEDGER endpoints ────────────────────────────────────

  /** GET /api/refunds?visitId=N — list refunds linked to a visit */
  getRefundsByVisit(visitId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/refunds`, { params: { visitId: String(visitId) } });
  }

  /** GET /api/refunds/pending — refunds awaiting Billing to disburse */
  getPendingRefunds(): Observable<any> {
    return this.http.get(`${this.baseUrl}/refunds/pending`);
  }

  /** GET /api/refunds/overpayments — visits where SUM(collected) > SUM(charges),
   *  surfaced on Pending Refunds so cashiers can refund the excess. */
  getOverpayments(): Observable<any> {
    return this.http.get(`${this.baseUrl}/refunds/overpayments`);
  }

  /** GET /api/refunds/awaiting-approval — maker-checker queue: Requested (non-lab)
   *  refunds, e.g. pharmacist reverse-dispense, awaiting a second-person approval. */
  getRefundsAwaitingApproval(): Observable<any> {
    return this.http.get(`${this.baseUrl}/refunds/awaiting-approval`);
  }

  /** POST /api/refunds/{id}/approve — CHECKER approves a Requested refund → Approved.
   *  The server enforces approver ≠ requester (maker-checker). */
  approveRefund(refundId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/refunds/${refundId}/approve`, {});
  }

  /** GET /api/refunds?status=Requested — Lab queue of pending sample-state attestations */
  getRefundsByStatus(status: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/refunds`, { params: { status } });
  }

  /** POST /api/refunds/{id}/process — Billing/Receptionist disburses cash */
  processRefund(refundId: number, mode: string, reference?: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/refunds/${refundId}/process`, { mode, reference });
  }

  /** POST /api/refunds/{id}/authorize — HospitalAdmin signs off above-threshold */
  authorizeRefund(refundId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/refunds/${refundId}/authorize`, {});
  }

  /** POST /api/refunds/{id}/reject — Lab/Admin denies the refund */
  rejectRefund(refundId: number, reason: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/refunds/${refundId}/reject`, { reason });
  }

  /** POST /api/refunds/{id}/lab-confirm — Lab attests sample state */
  labConfirmRefund(refundId: number,
    sampleState: 'NotCollected' | 'CollectedNotProcessed' | 'PartiallyProcessed' | 'MostlyProcessed' | 'AlreadyProcessed'
  ): Observable<any> {
    return this.http.post(`${this.baseUrl}/refunds/${refundId}/lab-confirm`, { sampleState });
  }

  /** PATCH /api/test-requests/{id}/reschedule */
  rescheduleTest(testRequestId: number, newDate: string): Observable<any> {
    return this.http.patch(`${this.baseUrl}/test-requests/${testRequestId}/reschedule`, { newDate });
  }

  // ═══════════════════════════════════════════
  // IP BILLING APIs
  // ═══════════════════════════════════════════

  /** Get all currently admitted patients with running bill totals */
  getAdmittedPatients(): Observable<any> {
    return this.http.get(`${this.baseUrl}/ip-billing/admitted-patients`);
  }

  /** Get running bill summary for an admission */
  getRunningIPBill(admissionId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/ip-billing/running-bill/${admissionId}`);
  }

  /** Get ward tariffs for the hospital */
  getWardTariffs(): Observable<any> {
    return this.http.get(`${this.baseUrl}/ip-billing/tariffs`);
  }

  /** Create or update a ward tariff */
  saveWardTariff(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ip-billing/tariffs`, body);
  }

  /** Retire (soft-delete) a ward tariff — stops it applying to new stays; past bills unaffected */
  deactivateWardTariff(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/ip-billing/tariffs/${id}`);
  }

  /** Transfer patient to a different bed/ward */
  transferBed(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ip-billing/transfer`, body);
  }

  /** Get bed transfer history for an admission */
  getTransferHistory(admissionId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/ip-billing/transfers/${admissionId}`);
  }

  /** Collect an advance payment */
  collectAdvance(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ip-billing/advance`, body);
  }

  /** Get advance payments for a visit */
  getAdvances(visitId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/ip-billing/advance/${visitId}`);
  }

  /** Refund an advance payment */
  refundAdvance(advanceId: number, body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ip-billing/advance/${advanceId}/refund`, body);
  }

  /** Add an ad-hoc IP charge */
  addIPCharge(body: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/ip-billing/charge`, body);
  }

  /** Void (soft-delete) an IP charge */
  voidIPCharge(chargeId: number, body: any): Observable<any> {
    return this.http.delete(`${this.baseUrl}/ip-billing/charge/${chargeId}`, { body });
  }

  /** Get IP charges for an admission */
  getIPCharges(admissionId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/ip-billing/charges/${admissionId}`);
  }

  /** Reopen billing after premature discharge */
  reopenBilling(admissionId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/ip-billing/reopen/${admissionId}`, {});
  }

  // ── HEALTH TIMELINE APIs ──────────────────────────────────────────

  /** GET /api/health-timeline/{patientId} */
  getHealthTimeline(patientId: number, months: number = 12): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/health-timeline/${patientId}?months=${months}`);
  }

  /** GET /api/health-timeline/{patientId}/summary */
  getHealthSummary(patientId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/health-timeline/${patientId}/summary`);
  }

  // ── MEDICINE STOCK APIs ──────────────────────────────────────────

  /** GET /api/medicine-stock */
  getMedicineStock(search?: string, filter?: string, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (filter) params = params.set('filter', filter);
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.get<any>(`${this.baseUrl}/medicine-stock`, { params });
  }

  /** GET /api/medicine-stock/alerts */
  getMedicineStockAlerts(hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.get<any>(`${this.baseUrl}/medicine-stock/alerts`, { params });
  }

  /** GET /api/medicine-stock/summary */
  getMedicineStockSummary(hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.get<any>(`${this.baseUrl}/medicine-stock/summary`, { params });
  }

  /** POST /api/medicine-stock */
  addMedicineStock(body: any, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.post<any>(`${this.baseUrl}/medicine-stock`, body, { params });
  }

  /** PUT /api/medicine-stock/{id} */
  updateMedicineStock(id: number, body: any, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.put<any>(`${this.baseUrl}/medicine-stock/${id}`, body, { params });
  }

  /** DELETE /api/medicine-stock/{id} */
  deleteMedicineStock(id: number, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.delete<any>(`${this.baseUrl}/medicine-stock/${id}`, { params });
  }

  /** PATCH /api/medicine-stock/{id}/restock */
  restockMedicine(id: number, body: any, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.patch<any>(`${this.baseUrl}/medicine-stock/${id}/restock`, body, { params });
  }

  /** PATCH /api/medicine-stock/{id}/dispense */
  dispenseMedicineStock(id: number, body: any, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.patch<any>(`${this.baseUrl}/medicine-stock/${id}/dispense`, body, { params });
  }

  /** GET /api/medicine-stock/{id}/movements */
  getMedicineMovements(id: number, hospitalId?: number): Observable<any> {
    let params = new HttpParams();
    if (hospitalId) params = params.set('hospitalId', hospitalId.toString());
    return this.http.get<any>(`${this.baseUrl}/medicine-stock/${id}/movements`, { params });
  }

  // ─────────────────── PHARMACY DISPENSE LEDGER ───────────────────

  /** POST /api/pharmacy/dispense — record a partial/full dispense event (with optional substitution) */
  createDispense(body: {
    prescriptionId: number;
    medicineStockId?: number | null;
    dispensedDrugName: string;
    dispensedDosage?: string;
    dispensedFormType?: string;
    dispensedQuantity: number;
    unitPrice: number;
    isSubstitution?: boolean;
    substitutionReason?: string;
    pharmacistNotes?: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/pharmacy/dispense`, body);
  }

  /** POST /api/pharmacy/dispense/close-remaining */
  closeRemainingDispense(body: { prescriptionId: number; reason?: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/pharmacy/dispense/close-remaining`, body);
  }

  /** POST /api/pharmacy/dispense/reverse */
  reverseDispense(body: { dispenseId: number; reason?: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/pharmacy/dispense/reverse`, body);
  }

  /** GET /api/pharmacy/dispense/by-visit/{visitId} */
  getDispensesByVisit(visitId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/pharmacy/dispense/by-visit/${visitId}`);
  }

  /** GET /api/pharmacy/dispense/by-prescription/{prescriptionId} */
  getDispensesByPrescription(prescriptionId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/pharmacy/dispense/by-prescription/${prescriptionId}`);
  }

  /** GET /api/pharmacy/dispense/prescription/{prescriptionId} — prescription + ledger roll-up */
  getPrescriptionWithDispenses(prescriptionId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/pharmacy/dispense/prescription/${prescriptionId}`);
  }

  /** GET /api/medicine-stock/search?q= */
  searchMedicineStock(query: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/medicine-stock/search?q=${encodeURIComponent(query)}`);
  }

  /** GET /api/medicine-stock/lookup?drug= — single-drug lookup, returns exactMatch + candidates + stockStatus.
   *  Used by dispense modal stock chip and doctor prescribing stock-check. */
  lookupMedicineStock(drug: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/medicine-stock/lookup?drug=${encodeURIComponent(drug)}`);
  }

  /** GET /api/medicine-stock/all-hospitals — SuperAdmin cross-hospital stock overview */
  getAllHospitalsStock(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/medicine-stock/all-hospitals`);
  }

  /** GET /api/medicine-stock/hospital/{id} — SuperAdmin view specific hospital stock */
  getHospitalStock(hospitalId: number, search?: string, filter?: string): Observable<any> {
    let url = `${this.baseUrl}/medicine-stock/hospital/${hospitalId}`;
    const params: string[] = [];
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (filter) params.push(`filter=${filter}`);
    if (params.length) url += '?' + params.join('&');
    return this.http.get<any>(url);
  }

  /** POST /api/medicine-stock/bulk-seed — Bulk seed stock for a hospital */
  bulkSeedStock(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/medicine-stock/bulk-seed`, data);
  }

  /** POST /api/medicine-stock/seed-common — Seed 30 common Indian medicines (one-click) */
  seedCommonMedicines(hospitalId?: number): Observable<any> {
    const url = hospitalId
      ? `${this.baseUrl}/medicine-stock/seed-common?hospitalId=${hospitalId}`
      : `${this.baseUrl}/medicine-stock/seed-common`;
    return this.http.post<any>(url, {});
  }

  // ── INSURANCE CLAIMS APIs ──────────────────────────────────────────

  /** GET /api/insurance */
  getInsuranceClaims(status?: string, page = 1, pageSize = 20): Observable<any> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (status) params = params.set('status', status);
    return this.http.get<any>(`${this.baseUrl}/insurance`, { params });
  }

  /** GET /api/insurance/visit/{visitId} */
  getInsuranceByVisit(visitId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/insurance/visit/${visitId}`);
  }

  /** GET /api/insurance/summary */
  getInsuranceSummary(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/insurance/summary`);
  }

  /** GET /api/insurance/providers */
  getInsuranceProviders(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/insurance/providers`);
  }

  /** POST /api/insurance */
  createInsuranceClaim(body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/insurance`, body);
  }

  /** PATCH /api/insurance/{id}/submit */
  submitInsuranceClaim(id: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/insurance/${id}/submit`, body);
  }

  /** PATCH /api/insurance/{id}/approve */
  approveInsuranceClaim(id: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/insurance/${id}/approve`, body);
  }

  /** PATCH /api/insurance/{id}/reject */
  rejectInsuranceClaim(id: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/insurance/${id}/reject`, body);
  }

  /** PATCH /api/insurance/{id}/settle — body carries the actual receipt {receipt, treatment?}. */
  settleInsuranceClaim(id: number, body: any = {}): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/insurance/${id}/settle`, body);
  }

  // ═══════════════════════════════════════════
  // INSURANCE PRE-AUTHORIZATION (cashless) APIs
  // ═══════════════════════════════════════════

  /** GET /api/preauth */
  getPreAuths(status?: string, page = 1, pageSize = 20): Observable<any> {
    let params: any = { page, pageSize };
    if (status) params.status = status;
    return this.http.get<any>(`${this.baseUrl}/preauth`, { params });
  }

  /** GET /api/preauth/summary */
  getPreAuthSummary(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/preauth/summary`);
  }

  /** GET /api/preauth/visit/{visitId} — the active pre-auth (with enhancements) for a visit, or null. */
  getPreAuthByVisit(visitId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/preauth/visit/${visitId}`);
  }

  /** POST /api/preauth — create a pre-authorization request. */
  createPreAuth(body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/preauth`, body);
  }

  /** PATCH /api/preauth/{id}/submit */
  submitPreAuth(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/preauth/${id}/submit`, {});
  }

  /** PATCH /api/preauth/{id}/approve — {authNumber, sanctionedAmount, validUntil?, notes?}. */
  approvePreAuth(id: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/preauth/${id}/approve`, body);
  }

  /** PATCH /api/preauth/{id}/reject — {reason}. */
  rejectPreAuth(id: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/preauth/${id}/reject`, body);
  }

  /** PATCH /api/preauth/{id}/cancel */
  cancelPreAuth(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/preauth/${id}/cancel`, {});
  }

  /** POST /api/preauth/{id}/enhancements — request a mid-stay top-up {requestedAmount, reason?}. */
  requestPreAuthEnhancement(id: number, body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/preauth/${id}/enhancements`, body);
  }

  /** PATCH /api/preauth/enhancements/{enhancementId}/decide — {approve, approvedAmount, authNumber?, reason?}. */
  decidePreAuthEnhancement(enhancementId: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/preauth/enhancements/${enhancementId}/decide`, body);
  }

  // ═══════════════════════════════════════════
  // TELEMEDICINE APIs
  // ═══════════════════════════════════════════

  /** POST /api/telemedicine/create */
  createTelemedicineSession(body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/telemedicine/create`, body);
  }

  /** GET /api/telemedicine/visit/{visitId} */
  getTelemedicineByVisit(visitId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/telemedicine/visit/${visitId}`);
  }

  /** GET /api/telemedicine/visit/{visitId}/sessions — all teleconsult sessions for a visit (history) */
  getTelemedicineSessionsByVisit(visitId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/telemedicine/visit/${visitId}/sessions`);
  }

  /** GET /api/telemedicine/active */
  getActiveTelemedicineSessions(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/telemedicine/active`);
  }

  /** PATCH /api/telemedicine/{id}/start */
  startTelemedicineSession(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/telemedicine/${id}/start`, {});
  }

  /** PATCH /api/telemedicine/{id}/end */
  endTelemedicineSession(id: number, body?: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/telemedicine/${id}/end`, body || {});
  }

  /** PATCH /api/telemedicine/{id}/cancel */
  cancelTelemedicineSession(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/telemedicine/${id}/cancel`, {});
  }

  // ── CONSENT MANAGEMENT (DPDP Act 2023) ─────────────────────────
  getPatientConsent(patientId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/consent/${patientId}`);
  }

  updatePatientConsent(patientId: number, consent: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/consent/${patientId}`, consent);
  }

  /** WhatsApp consent after registration (2026-09-28): { agreed, agreedAt, changedAt, changedBy }. */
  getWhatsAppConsent(patientId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/consent/${patientId}/whatsapp`);
  }

  setWhatsAppConsent(patientId: number, agreed: boolean, note?: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/consent/${patientId}/whatsapp`, { agreed, note: note || null });
  }

  withdrawPatientConsent(patientId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/consent/${patientId}/withdraw`, {});
  }

  // ── DATA DELETION (Right to Erasure) ────────────────────────────
  requestPatientDeletion(patientId: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/deletion/${patientId}`);
  }

  // ── SUBSCRIPTION & BILLING (Phase 0) ─────────────────────

  // Subscriptions
  getAllSubscriptions() {
    return this.http.get<any[]>(`${this.baseUrl}/subscriptions`);
  }

  getSubscription(hospitalId: number) {
    return this.http.get<any>(`${this.baseUrl}/subscriptions/hospital/${hospitalId}`);
  }

  getMySubscription() {
    return this.http.get<any>(`${this.baseUrl}/subscriptions/my`).pipe(
      catchError(() => of({ plan: 'Free', isActive: true }))
    );
  }

  createSubscription(data: any) {
    return this.http.post<any>(`${this.baseUrl}/subscriptions`, data);
  }

  updateSubscription(id: number, data: any) {
    return this.http.put<any>(`${this.baseUrl}/subscriptions/${id}`, data);
  }

  cancelSubscription(id: number) {
    return this.http.delete(`${this.baseUrl}/subscriptions/${id}`);
  }

  getBillingDashboard() {
    return this.http.get<any>(`${this.baseUrl}/subscriptions/dashboard`);
  }

  // Platform Invoices
  generateInvoice(hospitalId: number, month: string) {
    return this.http.post<any>(`${this.baseUrl}/platform-billing/invoices/generate`, { hospitalId, month });
  }

  getAllInvoices(status?: string) {
    const params = status ? `?status=${status}` : '';
    return this.http.get<any[]>(`${this.baseUrl}/platform-billing/invoices${params}`);
  }

  /** Billing audit 2026-10-17 (S14): the API now refuses "mark paid" without a payment reference (UTR / UPI ref / cheque no.). */
  markInvoicePaid(id: number, reference: string, notes?: string) {
    return this.http.patch<any>(`${this.baseUrl}/platform-billing/invoices/${id}/pay`, { reference, notes });
  }

  markOverdueInvoices() {
    return this.http.post<any>(`${this.baseUrl}/platform-billing/invoices/mark-overdue`, {});
  }

  // Usage Metrics
  getUsage(hospitalId: number, month: string) {
    return this.http.get<any>(`${this.baseUrl}/platform-billing/usage/${hospitalId}?month=${month}`);
  }

  getAllUsage(month: string) {
    return this.http.get<any[]>(`${this.baseUrl}/platform-billing/usage?month=${month}`);
  }

  getUsageHistory(hospitalId: number, metricType: string, months: number = 6) {
    return this.http.get<any[]>(`${this.baseUrl}/platform-billing/usage/${hospitalId}/history?metricType=${metricType}&months=${months}`);
  }

  // ── Owner billing (SuperAdmin audit 2026-09-27) ─────────────────────────────
  // Payments ledger, void, PDF, billing run, billing profile, hospital console, hospital access.
  getInvoicePayments(invoiceId: number) {
    return this.http.get<any[]>(`${this.baseUrl}/platform-billing/invoices/${invoiceId}/payments`);
  }

  recordInvoicePayment(invoiceId: number, body: { saveKey?: string; amount: number; tdsAmount: number; mode: string; reference?: string; paidOn?: string; notes?: string }) {
    return this.http.post<any>(`${this.baseUrl}/platform-billing/invoices/${invoiceId}/payments`, body);
  }

  reversePlatformPayment(paymentId: number, reason: string) {
    return this.http.post<any>(`${this.baseUrl}/platform-billing/payments/${paymentId}/reverse`, { reason });
  }

  voidPlatformInvoice(invoiceId: number, reason: string) {
    return this.http.post<any>(`${this.baseUrl}/platform-billing/invoices/${invoiceId}/void`, { reason });
  }

  /** The GST invoice PDF as a Blob (auth header rides on the interceptor; opened via an object URL). */
  downloadPlatformInvoicePdf(invoiceId: number) {
    return this.http.get(`${this.baseUrl}/platform-billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  }

  previewBillingRun(month: string, hospitalId?: number) {
    const h = hospitalId ? `&hospitalId=${hospitalId}` : '';
    return this.http.get<any>(`${this.baseUrl}/platform-billing/billing-run/preview?month=${month}${h}`);
  }

  runBilling(month: string, hospitalId?: number) {
    return this.http.post<any>(`${this.baseUrl}/platform-billing/billing-run`, { month, hospitalId: hospitalId || null });
  }

  getBillingProfile() {
    return this.http.get<any>(`${this.baseUrl}/platform-billing/profile`);
  }

  updateBillingProfile(body: any) {
    return this.http.put<any>(`${this.baseUrl}/platform-billing/profile`, body);
  }

  /** Hospital admin: their own plan, invoices, total due and how to pay. */
  getMyPlatformBilling() {
    return this.http.get<any>(`${this.baseUrl}/platform-billing/my`);
  }

  // ── Hospital feedback / rating about MediTrack (2026-10-11) ──
  /** Signed-in hospital user rates MediTrack (1–5 + optional comment) for their hospital. */
  submitFeedback(rating: number, comment: string) {
    return this.http.post<any>(`${this.baseUrl}/feedback`, { rating, comment });
  }

  /** This hospital's own submitted feedback (auto-scoped to the tenant). */
  getMyFeedback() {
    return this.http.get<any>(`${this.baseUrl}/feedback/mine`);
  }

  /** Owner only: every hospital's feedback, newest first. */
  getAllFeedback() {
    return this.http.get<any>(`${this.baseUrl}/feedback`);
  }

  /** Any signed-in user: the hospital's access state for the app-wide banner. */
  getMyHospitalAccess() {
    return this.http.get<any>(`${this.baseUrl}/hospital-access/my`, { headers: { 'X-No-Toast': 'true' } });
  }

  // ── User journeys (owner only, 2026-09-29) ──
  /** People who hit a problem (browser error, request that never arrived, server failure) in the last `hours`. */
  getJourneyProblems(hours = 24, hospitalId?: number) {
    let params = new HttpParams().set('hours', hours);
    if (hospitalId) params = params.set('hospitalId', hospitalId);
    return this.http.get<any>(`${this.baseUrl}/platform/journeys/problems`, { params });
  }

  findJourneyStaff(hospitalId: number, q: string) {
    return this.http.get<any>(`${this.baseUrl}/platform/journeys/staff`, { params: new HttpParams().set('hospitalId', hospitalId).set('q', q || '') });
  }

  /** One person's steps between `from` and `to` (IST, "yyyy-MM-ddTHH:mm"); default the last 24 hours. */
  getJourney(staffId: number, from?: string, to?: string) {
    let params = new HttpParams().set('staffId', staffId);
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return this.http.get<any>(`${this.baseUrl}/platform/journeys`, { params });
  }

  // ── Personal email for password reset (signed-in staff, 2026-09-29) ──
  getRecoveryEmail() {
    return this.http.get<any>(`${this.baseUrl}/auth/recovery-email`);
  }
  // DISC-013: every write carries the current password (counted with the lock screen and Change Password).
  startRecoveryEmail(email: string, currentPassword: string) {
    return this.http.post<any>(`${this.baseUrl}/auth/recovery-email`, { email, currentPassword }, { headers: { 'X-No-Toast': 'true' } });
  }
  verifyRecoveryEmail(code: string, currentPassword: string) {
    return this.http.post<any>(`${this.baseUrl}/auth/recovery-email/verify`, { code, currentPassword }, { headers: { 'X-No-Toast': 'true' } });
  }
  removeRecoveryEmail(currentPassword: string) {
    return this.http.delete<any>(`${this.baseUrl}/auth/recovery-email`, { body: { currentPassword }, headers: { 'X-No-Toast': 'true' } });
  }

  // Owner hospital console
  getPlatformHospital(id: number) {
    return this.http.get<any>(`${this.baseUrl}/platform/hospitals/${id}`);
  }

  updatePlatformHospital(id: number, body: any) {
    return this.http.put<any>(`${this.baseUrl}/platform/hospitals/${id}`, body);
  }

  setHospitalAccess(id: number, status: string, reason: string) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${id}/access`, { status, reason });
  }

  setHospitalBillingOverrides(id: number, autoRestrictExempt: boolean, autoRestrictAfterDays: number | null) {
    return this.http.put<any>(`${this.baseUrl}/platform/hospitals/${id}/billing-overrides`, { autoRestrictExempt, autoRestrictAfterDays });
  }

  ownerResetAdminPassword(hospitalId: number, staffId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/admins/${staffId}/reset-password`, {});
  }

  // Hospital offboarding (owner) + data export
  startOffboarding(hospitalId: number, reason: string, noticeDays: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/offboarding/start`, { reason, noticeDays });
  }

  cancelOffboarding(hospitalId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/offboarding/cancel`, {});
  }

  endOffboardingNow(hospitalId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/offboarding/end-now`, {});
  }

  previewHospitalDeletion(hospitalId: number) {
    return this.http.get<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/offboarding/preview`);
  }

  deleteHospitalData(hospitalId: number, confirmCode: string, deleteWithoutExport: boolean) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/offboarding/delete`, { confirmCode, deleteWithoutExport });
  }

  downloadHospitalExport(hospitalId: number) {
    return this.http.get(`${this.baseUrl}/platform/hospitals/${hospitalId}/export`, { responseType: 'blob', observe: 'response', headers: { 'X-Timeout': '900000' } });   // TC-5994
  }

  /** Finish removing a deleted hospital's documents after file storage was unreachable. */
  retryHospitalFileRemoval(hospitalId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/offboarding/remove-files`, {});
  }

  downloadDeletionCertificate(hospitalId: number) {
    return this.http.get(`${this.baseUrl}/platform/hospitals/${hospitalId}/deletion-certificate`, { responseType: 'blob' });
  }

  /** Hospital admin: full copy of their hospital's data (ZIP). */
  downloadMyDataExport() {
    return this.http.get(`${this.baseUrl}/platform-billing/my/export`, { responseType: 'blob', observe: 'response', headers: { 'X-Timeout': '900000' } });   // TC-5994: a big hospital's ZIP takes minutes
  }

  // Online payments (Razorpay) + owner metrics
  /** Payment link for an invoice's current balance — owner, or the invoice's own hospital admin. */
  getInvoicePayLink(invoiceId: number) {
    return this.http.get<{ url: string }>(`${this.baseUrl}/platform-billing/invoices/${invoiceId}/pay-link`);
  }

  setupAutopay(hospitalId: number) {
    return this.http.post<{ url: string }>(`${this.baseUrl}/platform/hospitals/${hospitalId}/autopay`, {});
  }

  cancelAutopay(hospitalId: number) {
    return this.http.delete<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/autopay`);
  }

  getPlatformMetrics() {
    return this.http.get<any>(`${this.baseUrl}/platform-billing/metrics`);
  }

  // Owner account safety. DISC-034: the Owner security page shows these replies in its own box (the page's red box, or
  // the password dialog's), so they carry X-No-Toast — each refusal showed twice (as TC-0176 fixed for Settings → Security).
  getOwners() {
    return this.http.get<any[]>(`${this.baseUrl}/platform/owners`);
  }

  getMyOwnerSecurity() {
    return this.http.get<any>(`${this.baseUrl}/platform/owners/me/security`, { headers: { 'X-No-Toast': 'true' } });
  }

  createOwner(fullName: string, email: string, currentPassword: string) {
    return this.http.post<any>(`${this.baseUrl}/platform/owners`, { fullName, email, currentPassword }, { headers: { 'X-No-Toast': 'true' } });
  }

  setOwnerActive(id: number, active: boolean, currentPassword: string) {
    return this.http.post<any[]>(`${this.baseUrl}/platform/owners/${id}/${active ? 'reactivate' : 'deactivate'}`, { currentPassword },
      { headers: { 'X-No-Toast': 'true' } });
  }

  generateRecoveryCodes(currentPassword: string) {
    return this.http.post<any>(`${this.baseUrl}/platform/owners/me/recovery-codes`, { currentPassword }, { headers: { 'X-No-Toast': 'true' } });
  }

  ownerResetAdminMfa(hospitalId: number, staffId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/admins/${staffId}/reset-mfa`, {});
  }

  // DISC-015: one-time authenticator setup codes (shown once, never emailed)
  ownerIssueAdminSetupCode(hospitalId: number, staffId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/admins/${staffId}/mfa-setup-code`, {});
  }

  issueOwnerSetupCode(id: number, currentPassword: string) {   // DISC-034: the password dialog shows its refusal
    return this.http.post<any>(`${this.baseUrl}/platform/owners/${id}/setup-code`, { currentPassword }, { headers: { 'X-No-Toast': 'true' } });
  }

  // Owner landing: hospital health
  getPlatformHealth() {
    return this.http.get<any>(`${this.baseUrl}/platform/hospitals/health`);
  }

  // Owner announcements / maintenance banners
  getAnnouncements() {
    return this.http.get<any[]>(`${this.baseUrl}/platform/announcements`);
  }

  createAnnouncement(body: any) {
    return this.http.post<any>(`${this.baseUrl}/platform/announcements`, body);
  }

  updateAnnouncement(id: number, body: any) {
    return this.http.put<any>(`${this.baseUrl}/platform/announcements/${id}`, body);
  }

  endAnnouncement(id: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/announcements/${id}/end`, {});
  }

  /** Any staff member closes a banner (stays closed on every device). */
  dismissAnnouncement(id: number) {
    return this.http.post<any>(`${this.baseUrl}/hospital-access/announcements/${id}/dismiss`, {}, { headers: { 'X-No-Toast': 'true' } });
  }

  // Support access — owner side (asks; opens the session once approved)
  requestSupportAccess(hospitalId: number, reason: string, minutes: number, scope: string) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/support-requests`, { reason, minutes, scope });
  }

  cancelSupportAccess(hospitalId: number, grantId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/support-requests/${grantId}/cancel`, {});
  }

  openSupportSession(hospitalId: number, grantId: number) {
    return this.http.post<any>(`${this.baseUrl}/platform/hospitals/${hospitalId}/support-requests/${grantId}/session`, {});
  }

  // Support access — hospital admin side (answers, watches, ends)
  getSupportAccess() {
    return this.http.get<any[]>(`${this.baseUrl}/hospital-access/support`);
  }

  getSupportActivity(grantId: number) {
    return this.http.get<any[]>(`${this.baseUrl}/hospital-access/support/${grantId}/activity`);
  }

  approveSupportAccess(grantId: number, allowChanges: boolean, note: string) {
    return this.http.post<any[]>(`${this.baseUrl}/hospital-access/support/${grantId}/approve`, { allowChanges, note });
  }

  denySupportAccess(grantId: number, note: string) {
    return this.http.post<any[]>(`${this.baseUrl}/hospital-access/support/${grantId}/deny`, { allowChanges: false, note });
  }

  endSupportAccess(grantId: number) {
    return this.http.post<any[]>(`${this.baseUrl}/hospital-access/support/${grantId}/end`, {});
  }

  // ══════════════════════════════════════════════════════════════
  //  STAFF LEAVE MANAGEMENT
  // ══════════════════════════════════════════════════════════════

  applyLeave(body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/leave/apply`, body);
  }

  getMyLeaves(year?: number): Observable<any> {
    let params = new HttpParams();
    if (year) params = params.set('year', year.toString());
    return this.http.get<any>(`${this.baseUrl}/leave/my`, { params });
  }

  getTodayLeaves(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/leave/today`);
  }

  getAllLeaves(filters?: { status?: string; staffId?: number; month?: number; year?: number }): Observable<any> {
    let params = new HttpParams();
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.staffId) params = params.set('staffId', filters.staffId.toString());
    if (filters?.month) params = params.set('month', filters.month.toString());
    if (filters?.year) params = params.set('year', filters.year.toString());
    return this.http.get<any>(`${this.baseUrl}/leave`, { params });
  }

  approveLeave(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/leave/${id}/approve`, {});
  }

  rejectLeave(id: number, rejectionReason?: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/leave/${id}/reject`, { rejectionReason });
  }

  cancelLeave(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/leave/${id}/cancel`, {});
  }

  getLeaveSummary(year?: number): Observable<any> {
    let params = new HttpParams();
    if (year) params = params.set('year', year.toString());
    return this.http.get<any>(`${this.baseUrl}/leave/summary`, { params });
  }

  // ══════════════════════════════════════════════════════════════
  //  STAFF DIRECTORY
  // ══════════════════════════════════════════════════════════════

  getStaffDirectory(filters?: { search?: string; role?: string; department?: string }): Observable<any> {
    let params = new HttpParams();
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.role) params = params.set('role', filters.role);
    if (filters?.department) params = params.set('department', filters.department);
    return this.http.get<any>(`${this.baseUrl}/staff-directory`, { params });
  }

  getStaffProfile(staffId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/staff-directory/${staffId}`);
  }

  updateMyProfile(body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/staff-directory/my-profile`, body);
  }

  uploadProfilePhoto(file: File): Observable<any> {
    const fd = new FormData();
    fd.append('file', file);
    // TC-6100: give the upload a long timeout (5 min) so a photo on a slow link is not cut off at 30s.
    const headers = new HttpHeaders({ 'X-Timeout': '300000' });
    return this.http.post<any>(`${this.baseUrl}/staff-directory/my-profile/photo`, fd, { headers });
  }

  updateAvailability(body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/staff-directory/availability`, body);
  }

  updateStaffExtended(staffId: number, body: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/staff-directory/${staffId}/extended`, body);
  }

  getDepartments(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/staff-directory/departments`);
  }

  // Attendance
  checkIn(body?: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/staff-directory/attendance/check-in`, body || {});
  }

  checkOut(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/staff-directory/attendance/check-out`, {});
  }

  getMyAttendance(month?: number, year?: number): Observable<any> {
    let params = new HttpParams();
    if (month) params = params.set('month', month.toString());
    if (year) params = params.set('year', year.toString());
    return this.http.get<any>(`${this.baseUrl}/staff-directory/attendance/my`, { params });
  }

  getAllAttendance(date?: string): Observable<any> {
    let params = new HttpParams();
    if (date) params = params.set('date', date);
    return this.http.get<any>(`${this.baseUrl}/staff-directory/attendance`, { params });
  }

  // Shifts
  getShifts(startDate?: string, endDate?: string): Observable<any> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<any>(`${this.baseUrl}/staff-directory/shifts`, { params });
  }

  createShift(body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/staff-directory/shifts`, body);
  }

  bulkCreateShifts(shifts: any[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/staff-directory/shifts/bulk`, { shifts });
  }

  deleteShift(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/staff-directory/shifts/${id}`);
  }

  // On-Call
  getOnCallSchedule(date?: string): Observable<any> {
    let params = new HttpParams();
    if (date) params = params.set('date', date);
    return this.http.get<any>(`${this.baseUrl}/staff-directory/on-call`, { params });
  }

  getOnCallWeek(startDate?: string): Observable<any> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    return this.http.get<any>(`${this.baseUrl}/staff-directory/on-call/week`, { params });
  }

  createOnCall(body: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/staff-directory/on-call`, body);
  }

  deleteOnCall(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/staff-directory/on-call/${id}`);
  }

  // Org Chart
  getOrgChart(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/staff-directory/org-chart`);
  }

  // ── Receptionist: Open OP Visits Cleanup ────────────────────────────
  /** GET /api/visits/open-visits — OP visits still active after doctor marked Seen */
  getOpenVisits(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/visits/open-visits`);
  }

  /** POST /api/visits/bulk-close — Close stale OP visits */
  bulkCloseVisits(visitIds: number[], reason?: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/visits/bulk-close`, { visitIds, reason });
  }

  // ── ONBOARDING CHECKLIST ────────────────────────────────────────────────

  /** GET /api/onboarding/status — hospital setup readiness for checklist / dashboard banner.
   *  Cached for 5 minutes so Layout badge + Dashboard banner + the Onboarding page all share
   *  exactly ONE HTTP request rather than firing independently on every subscription. */
  getOnboardingStatus(hospitalId?: number): Observable<any> {
    const cacheKey = `onboarding-status-${hospitalId ?? 'self'}`;
    return this.getCached(
      cacheKey,
      () => {
        const params = hospitalId ? `?hospitalId=${hospitalId}` : '';
        return this.http.get<any>(`${this.baseUrl}/onboarding/status${params}`);
      },
      300_000 // 5-minute TTL — short enough to reflect real config changes
    );
  }

  /** Call after saving hospital settings, ward tariffs, tests, beds, staff, or medicine stock
   *  so the next getOnboardingStatus() re-fetches instead of returning stale cached data.
   *  QA audit 2026-09-25: a HospitalAdmin's onboarding view fetches under key 'onboarding-status-self'
   *  (it passes no hospitalId), while settings clears 'onboarding-status-<numericId>'. That mismatch left
   *  an admin's checklist stale for up to the 5-min TTL after a settings change. Clear BOTH keys so the
   *  refresh is reliable for admins and for SuperAdmins inspecting a specific hospital. */
  clearOnboardingCache(hospitalId?: number): void {
    this.clearCache('onboarding-status-self');
    if (hospitalId != null) this.clearCache(`onboarding-status-${hospitalId}`);
  }

}

